import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { IonButtons, IonRouterLink } from '@ionic/react'
import formatISO from 'date-fns/formatISO'
import { Navigation, Pagination, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { ReportButton } from '@components/molecules/ReportButton'
import { UserActivityButton } from '@components/molecules/UserActivityButton'
import { Share } from '@/components/atoms/assets/Share'
import { Avatar } from '@/components/atoms/Avatar'
import { MarkdownEllipsis } from '@/components/atoms/MarkdownEllipsis'
import { OriginChip } from '@/components/atoms/OriginChip'
import { UserName } from '@/components/atoms/UserName'
import { CommentsButton } from '@/components/molecules/CommentsButton'
import { LazyImg } from '@/components/molecules/LazyImg'
import { ReactionButton } from '@/components/molecules/ReactionButton'
import { ShareButton } from '@/components/molecules/ShareButton'
import { TapToLikeWrapper } from '@/components/molecules/TapToLikeWrapper'
import { IActivityItem } from '@/components/pages/Activity/interfaces'

import { appConfig } from '@services/config'
import { useMediaContainer } from '@/lib/hooks/useMediaContainer'
import { useReaction } from '@/lib/hooks/useReaction'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { Tracking, TrackingEvent } from '@/lib/tracking'
import { formatDistanceToNowDate } from '@/utils/date'
import { replaceUsernamesWithMentions } from '@/utils/replaceUsernamesWithMentions'
import {
  actorMarkdown,
  calculateContainerHeight,
  getMaxViewportWidth,
  safeCastUnknownToType,
} from '@/utils/utils'

import { Badge, FeedSections, Pages, ReportButtonType, Verb } from '@enums'

import { PostFeedSlider } from './PostFeedSlider'

export type IPostFeedActivityProps = IActivityItem & {
  attachments?: {
    images?: string[]
  }
  text?: string
  commentView?: boolean
  imageRatios?: number
  inViewport: (activityId: string) => void
  outViewport: (activityId: string) => void
  isActiveActivity: boolean
  index: number
  isScrollingFast?: boolean
}

export const PostFeedActivity: FC<IPostFeedActivityProps> = ({
  actor,
  attachments,
  text,
  time,
  verb,
  reaction_counts,
  object,
  id,
  commentView,
  imageRatios,
  inViewport,
  outViewport,
  isActiveActivity = true,
  index,
  isScrollingFast,
  origin,
  foreign_id,
}) => {
  const { t } = useTranslation('common')
  const { data: authUser } = useUserInfo()
  const { reaction, addReaction, deleteReaction } = useReaction(id)
  const hasOwnReactions = reaction?.ownReactions?.length > 0
  const [isZooming, setIsZooming] = useState(false)

  const history = useHistory()

  const containerHeight = useMemo(
    () =>
      calculateContainerHeight(
        object?.data?.media?.[0]?.ratio,
        getMaxViewportWidth()
      ),
    [object?.data?.media]
  )

  const handleDoubleClick = useCallback(async () => {
    if (hasOwnReactions || reaction?.isLoading) return

    addReaction({ activityId: id })

    Tracking.triggerEvent(TrackingEvent.PostLiked, {
      postId: id,
    })
  }, [hasOwnReactions, reaction?.isLoading, addReaction, id])

  const handleReactionClick = useCallback(async () => {
    if (hasOwnReactions) {
      const reactionId = reaction?.ownReactions?.[0]?.id as string | undefined

      deleteReaction({ reactionId })
      Tracking.triggerEvent(TrackingEvent.LikeDeleted, {
        activityId: id,
        contentId: (object?.data?.id || object?.id) ?? '',
        contentType: 'post',
        timestamp: formatISO(new Date()),
      })

      return
    }

    addReaction({ activityId: id })

    Tracking.triggerEvent(TrackingEvent.PostLiked, {
      postId: id,
    })
  }, [
    hasOwnReactions,
    addReaction,
    id,
    object?.data?.id,
    object?.id,
    reaction?.ownReactions,
    deleteReaction,
  ])

  const handleGoToComments = useCallback(() => {
    if (!window.location.href.includes(`/comments/${id}`)) {
      history.push(`/comments/${id}`)
    }
  }, [id, history])

  const { inView, mediaContainerRef, paddingBottom } = useMediaContainer({
    ratio: object?.data?.media?.[0].ratio,
    index,
  })

  const onUserActivityButtonSuccess = useCallback(() => {
    history.goBack()
  }, [history])

  const renderImage = useCallback(
    () =>
      attachments?.images?.length > 0 && (
        <div className="relative">
          <header className="w-full py-3 flex justify-start items-center">
            <IonRouterLink
              routerLink={`/profile/${actor.id}`}
              className="flex-none ml-3"
            >
              <Avatar
                userId={actor.id}
                isModerator={safeCastUnknownToType<boolean>(
                  actor?.data?.isModerator
                )}
                isAmbassador={safeCastUnknownToType<boolean>(
                  actor?.data?.isAmbassador
                )}
                badge={safeCastUnknownToType<Badge>(actor?.data?.badge)}
              />
            </IonRouterLink>
            <div className="font-bold ml-2.5 grow">
              <IonRouterLink routerLink={`/profile/${actor.id}`}>
                <UserName
                  name={safeCastUnknownToType<string>(actor?.data?.name)}
                />
              </IonRouterLink>
            </div>
            <div className="flex justify-end items-center">
              <IonButtons>
                {actor.id !== authUser?.id ? (
                  <ReportButton
                    type={ReportButtonType.Post}
                    confirmationLabel={t('okay')}
                    contestId={null}
                    activityId={id}
                    userId={actor.id}
                    isDisabled={actor.id === authUser?.id}
                  />
                ) : (
                  <UserActivityButton
                    type={ReportButtonType.Post}
                    activityId={id}
                    userId={authUser?.id}
                    onSuccess={onUserActivityButtonSuccess}
                    history={history}
                  />
                )}
              </IonButtons>
            </div>
          </header>
          {attachments?.images?.length > 1 ? (
            <Swiper
              style={{ overflowY: 'visible' }}
              modules={[Navigation, Pagination, Virtual]}
              direction="horizontal"
              className="feed"
              navigation
              pagination={!isZooming && { clickable: false }}
              virtual
            >
              {attachments?.images?.map((image, index: number) => (
                <SwiperSlide
                  key={image}
                  className={index === 0 ? 'bg-cool-gray-900' : 'bg-black'}
                  virtualIndex={index}
                >
                  <TapToLikeWrapper
                    handleDoubleClick={handleDoubleClick}
                    handleSingleClick={handleGoToComments}
                    isLikeActive={hasOwnReactions}
                  >
                    <div className="cursor-pointer">
                      <LazyImg
                        isLazy={false}
                        src={image}
                        imageAspectRatio={imageRatios?.[0]}
                        setIsZooming={setIsZooming}
                      />
                    </div>
                  </TapToLikeWrapper>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="relative grow bg-no-repeat bg-center flex flex-col overflow-hidden cursor-pointer">
              <TapToLikeWrapper
                handleDoubleClick={handleDoubleClick}
                handleSingleClick={handleGoToComments}
                isLikeActive={hasOwnReactions}
              >
                <LazyImg
                  isLazy={false}
                  src={attachments?.images?.[0]}
                  imageAspectRatio={imageRatios?.[0]}
                  setIsZooming={setIsZooming}
                />
              </TapToLikeWrapper>
            </div>
          )}
        </div>
      ),
    [
      attachments?.images,
      actor.id,
      authUser?.id,
      id,
      t,
      imageRatios,
      handleDoubleClick,
      handleGoToComments,
      actor?.data?.isModerator,
      actor?.data?.isAmbassador,
      actor?.data?.badge,
      actor?.data?.name,
      onUserActivityButtonSuccess,
      hasOwnReactions,
      history,
      isZooming,
    ]
  )

  const renderMedia = useCallback(() => {
    return (
      <div
        className="h-full w-full grow bg-cover relative"
        style={{ paddingBottom }}
      >
        <div className="absolute left-1/2 top-1/2 !w-full h-auto object-contain -translate-y-1/2 -translate-x-1/2">
          <PostFeedSlider
            media={object?.data?.media}
            isLikeActive={hasOwnReactions}
            handleDoubleClick={handleDoubleClick}
            inView={inView}
            paddingBottom={paddingBottom}
            isActiveActivity={isActiveActivity}
            containerHeight={containerHeight}
            mediaContainerRef={mediaContainerRef}
            activityId={id}
            itemId={object.id}
            isScrollingFast={isScrollingFast}
            isZooming={isZooming}
            setIsZooming={setIsZooming}
            foreignId={foreign_id}
          />
        </div>
      </div>
    )
  }, [
    paddingBottom,
    object?.data?.media,
    object.id,
    foreign_id,
    hasOwnReactions,
    handleDoubleClick,
    inView,
    isActiveActivity,
    containerHeight,
    mediaContainerRef,
    id,
    isScrollingFast,
    isZooming,
  ])

  const renderHeader =
    !attachments?.images?.length ||
    (object?.data?.media?.length && (
      <header className="flex justify-start items-center pt-[0.375rem] pb-3 relative !z-0">
        <IonRouterLink
          routerLink={`/profile/${actor.id}`}
          className="pl-3 flex-none"
        >
          <Avatar
            userId={actor.id}
            isModerator={safeCastUnknownToType<boolean>(
              actor?.data?.isModerator
            )}
            isAmbassador={safeCastUnknownToType<boolean>(
              actor?.data?.isAmbassador
            )}
            badge={safeCastUnknownToType<Badge>(actor?.data?.badge)}
          />
        </IonRouterLink>
        <div className="font-bold ml-2.5 flex justify-start items-center grow">
          <IonRouterLink
            routerLink={`/profile/${actor.id}`}
            className="cursor-pointer"
          >
            <UserName name={safeCastUnknownToType<string>(actor?.data?.name)} />
            <OriginChip origin={origin} />
          </IonRouterLink>
        </div>
        <div className="flex justify-end items-center">
          <IonButtons>
            {actor.id !== authUser?.id ? (
              <ReportButton
                type={ReportButtonType.Post}
                confirmationLabel={t('okay')}
                contestId={null}
                activityId={id}
                userId={actor.id}
                isDisabled={actor.id === authUser?.id}
              />
            ) : (
              <UserActivityButton
                type={ReportButtonType.Post}
                activityId={id}
                userId={authUser?.id}
                isDeletePostActive={typeof object !== 'string'}
                id={object?.data?.id}
                onSuccess={onUserActivityButtonSuccess}
                history={history}
              />
            )}
          </IonButtons>
        </div>
      </header>
    ))

  useEffect(() => {
    if (inView) {
      inViewport && inViewport(id)
      return
    }

    outViewport && outViewport(id)
  }, [inView, isActiveActivity, id, inViewport, outViewport])

  const markdownEllipsisText = useMemo(() => {
    const actorData = {
      url: `/profile/${actor.id}`,
      name: safeCastUnknownToType<string>(actor?.data?.name),
      isAmbassador: safeCastUnknownToType<boolean>(actor?.data?.isAmbassador),
      isModerator: safeCastUnknownToType<boolean>(actor?.data?.isModerator),
    }

    if (typeof object === 'object' && !!object?.data?.text) {
      const textWithMarkdown = replaceUsernamesWithMentions(
        safeCastUnknownToType<string>(object?.data?.text),
        object.data?.mentions
      )

      return `${actorMarkdown(actorData)} ${textWithMarkdown}`
    }

    if (typeof object === 'string' && !!text) {
      return `${actorMarkdown(actorData)} ${text}`
    }

    return null
  }, [actor, object, text])

  const handleShareClick = useCallback(() => {
    Tracking.triggerEvent(TrackingEvent.PostShared, {
      activityId: id,
      contentId: object?.data?.id,
      contentType: 'post',
      contentOwnerId: object?.data?.userId,
      userId: authUser?.id,
      timestamp: formatISO(new Date()),
    })
  }, [id, object?.data?.id, object?.data?.userId, authUser?.id])

  return (
    <div>
      {renderHeader}
      <div className="relative !z-10">
        {typeof object === 'string' ? renderImage() : renderMedia()}
      </div>
      <div className="relative !z-0">
        <div className="text-sm font-normal mx-3 mt-3 mb-1 whitespace-pre-line wordWrap-break text-cool-gray-200">
          <MarkdownEllipsis text={markdownEllipsisText} />
        </div>
        <div className="flex justify-between items-center ml-3 mb-3">
          <span className="block text-xs opacity-50 mb-3">
            {formatDistanceToNowDate({
              timestamp: String(time),
              addSuffix: false,
            })}
          </span>
          <IonButtons className="items-start">
            <ReactionButton
              onClick={handleReactionClick}
              likesNumberPosition="right"
              reactionActivityId={id}
            />
            {!commentView && (
              <CommentsButton
                color="plain"
                activityId={id}
                commentsNumber={reaction_counts?.COMMENT}
                commentsNumberPosition="right"
                page={Pages.Feed}
                channel={FeedSections.Post}
              />
            )}
            <ShareButton
              className="flex justify-center !p-0 cursor-pointer"
              subject={object?.data?.text}
              message={t('UGC.post.postBy', {
                userName: actor?.data?.name,
              })}
              buttonLabel={<Share className="cursor-pointer" />}
              url={`${appConfig.origin}/comments/${id}${
                verb === Verb.ContestParticipate ? `/video/${actor.id}` : ''
              }`}
              imageUrl={attachments?.images?.[0]}
              feature={`contest${
                verb === Verb.ContestParticipate ? '-video' : ''
              }`}
              params={{ refUsername: String(authUser?.username) }}
              onClickShare={handleShareClick}
            />
          </IonButtons>
        </div>
      </div>
    </div>
  )
}
